/**
 * crashCircuitBreaker
 *
 * Per-session, signature-keyed circuit breaker that caps how loudly a single
 * repeating crash can shout — both at the user (dialogs) and at telemetry
 * (`app:crash-occurred`). It sits on top of the SAME signature keying used by
 * `crashDedupService` (`${category}::${sanitizedMessage}`); callers pass the
 * signature in, so there is no parallel key space.
 *
 * Motivation: one user received 805 identical crash events in 28 days, another
 * 318 — nothing capped the dialog spam or the telemetry flood. This breaker
 * stops showing the dialog after MAX_DIALOGS_PER_SIGNATURE and stops firing
 * `app:crash-occurred` after MAX_TELEMETRY_FIRES_PER_SIGNATURE, replacing the
 * tail of a storm with at most one `app:crash-storm` summary per bucket.
 *
 * State is in-memory only and resets on app launch (and, in tests, via
 * `reset()` — which `crashDedupService.reset()` calls so the two stay in step).
 */

/** After this many dialogs for one signature, stop showing the dialog. */
export const MAX_DIALOGS_PER_SIGNATURE = 3;

/** After this many `app:crash-occurred` fires for one signature, stop firing. */
export const MAX_TELEMETRY_FIRES_PER_SIGNATURE = 10;

/** Coarse occurrence bucket a storming signature has reached. */
export type CrashStormBucket = '10-49' | '50-199' | '200+';

type BreakerEntry = {
  dialogsShown: number;
  telemetryFired: number;
  emittedBuckets: Set<CrashStormBucket>;
};

const entries = new Map<string, BreakerEntry>();

const getOrCreate = (signature: string): BreakerEntry => {
  const existing = entries.get(signature);
  if (existing) return existing;
  const entry: BreakerEntry = {
    dialogsShown: 0,
    telemetryFired: 0,
    emittedBuckets: new Set(),
  };
  entries.set(signature, entry);
  return entry;
};

/**
 * Map a signature's running occurrence total to its storm bucket. Counts below
 * the first bucket floor (the telemetry cap) return null — not yet a storm.
 */
export const bucketForOccurrenceCount = (count: number): CrashStormBucket | null => {
  if (count >= 200) return '200+';
  if (count >= 50) return '50-199';
  if (count >= MAX_TELEMETRY_FIRES_PER_SIGNATURE) return '10-49';
  return null;
};

/** True while this signature is still under its dialog cap. */
export const canShowDialog = (signature: string): boolean =>
  getOrCreate(signature).dialogsShown < MAX_DIALOGS_PER_SIGNATURE;

/** Record that a dialog was shown for this signature. */
export const noteDialogShown = (signature: string): void => {
  getOrCreate(signature).dialogsShown += 1;
};

export type TelemetryDecision = {
  /** Fire `app:crash-occurred` for this occurrence? */
  fireCrashOccurred: boolean;
  /** Non-null when this occurrence crosses into a not-yet-reported storm bucket. */
  stormBucket: CrashStormBucket | null;
};

/**
 * Record one occurrence of `signature` (whose running total is `occurrenceCount`)
 * and decide what telemetry to emit:
 *  - `fireCrashOccurred` stays true for the first MAX_TELEMETRY_FIRES_PER_SIGNATURE
 *    occurrences, then goes false.
 *  - `stormBucket` is set at most once per bucket, the first time the running
 *    total crosses that bucket's floor.
 */
export const recordTelemetryAttempt = (
  signature: string,
  occurrenceCount: number,
): TelemetryDecision => {
  const entry = getOrCreate(signature);

  let stormBucket: CrashStormBucket | null = null;
  const bucket = bucketForOccurrenceCount(occurrenceCount);
  if (bucket && !entry.emittedBuckets.has(bucket)) {
    entry.emittedBuckets.add(bucket);
    stormBucket = bucket;
  }

  let fireCrashOccurred = false;
  if (entry.telemetryFired < MAX_TELEMETRY_FIRES_PER_SIGNATURE) {
    entry.telemetryFired += 1;
    fireCrashOccurred = true;
  }

  return { fireCrashOccurred, stormBucket };
};

/** Session-boundary / test hook: clear all per-signature breaker state. */
export const reset = (): void => {
  entries.clear();
};
